import { z } from "zod";

// ─── Question ───────────────────────────────────────────────

export interface Question {
  id: string;
  unit: string;
  subunit: string;
  difficulty: 1 | 2 | 3;
  questionType?:
    | "true_false"
    | "four_choice"
    | "knowledge"
    | "reasoning"
    | "case"
    | "calculation";
  question: string;
  choices: string[];
  answer: number;
  explanation: string;
  memo?: string;
}

export interface QuestionFile {
  meta: {
    subject: string;
    curriculum: string;
    version: string;
    total: number;
  };
  questions: Question[];
}

// ─── Answer Record (forgetting curve) ───────────────────────

export interface AnswerRecord {
  lastCorrectDate: number; // Unix ms
  consecutiveCorrect: number;
}

// ─── Quiz Filter ────────────────────────────────────────────

export type QuizFilter = "all" | "bookmarked" | "wrongOnly" | "forgettingCurve";

// ─── Subject Config ─────────────────────────────────────────

export const SubjectConfigSchema = z.object({
  name: z.string(),
  nameJa: z.string(),
  primaryColor: z.string(),
  questionFileJa: z.string(),
  units: z.array(z.string()),
  totalQuestionCount: z.number(),
  shufflesChoiceOrder: z.boolean(),
  usesUnitModeSelection: z.boolean(),
  hasMockExam: z.boolean(),
});

export type SubjectConfig = z.infer<typeof SubjectConfigSchema>;

// ─── Last Lesson ────────────────────────────────────────────

export interface LastLesson {
  mode: string; // "quiz" | "memorize" | "readAloud"
  unit?: string;
  subunit?: string;
  difficulty?: number;
}

// ─── Study Pathway ──────────────────────────────────────────

export interface StudyPathway {
  id: string;
  title: string;
  subtitle: string;
  units: string[];
  icon: string;
  iconForeground: string;
  iconBackground: string;
}

// ─── Official Resource ──────────────────────────────────────

export interface OfficialResource {
  id: string;
  title: string;
  subtitle: string;
  url: string;
}

// ─── Lesson Structure (derived from question data) ──────────

export interface LessonLevel {
  difficulty: number;
  ids: string[];
}

export interface LessonSubunit {
  subunit: string;
  levels: LessonLevel[];
}

export interface LessonUnit {
  unit: string;
  lessons: LessonSubunit[];
}
